import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftRight, X } from 'lucide-react';

// Side-by-side control comparison across frameworks, restyled to the Threat Vector
// detail aesthetic. Comparison logic is unchanged.
const FrameworkComparison = ({
  primaryControl,
  primaryFramework,
  primaryDetails,
  secondaryControl,
  secondaryFramework,
  secondaryDetails,
  onClose,
}) => {
  const [keyDifferences, setKeyDifferences] = useState([]);

  const determineKeyDifferences = useCallback(() => {
    const differences = [];
    if (primaryDetails?.description && secondaryDetails?.description) {
      const has = (d) => d.includes('must') || d.includes('shall') || d.includes('requires');
      const p = has(primaryDetails.description);
      const s = has(secondaryDetails.description);
      if (p !== s) {
        differences.push({
          aspect: 'Requirement specificity',
          detail: p
            ? `${primaryFramework} has more specific requirements than ${secondaryFramework}`
            : `${secondaryFramework} has more specific requirements than ${primaryFramework}`,
        });
      }
    }
    if (primaryDetails && secondaryDetails) {
      const pf = primaryDetails.family || primaryDetails.section || '';
      const sf = secondaryDetails.family || secondaryDetails.section || '';
      if (pf !== sf) {
        differences.push({
          aspect: 'Categorisation',
          detail: `${primaryFramework} categorises this as "${pf}" while ${secondaryFramework} categorises it as "${sf}"`,
        });
      }
    }
    setKeyDifferences(differences);
  }, [primaryDetails, secondaryDetails, primaryFramework, secondaryFramework]);

  useEffect(() => {
    if (primaryDetails && secondaryDetails) determineKeyDifferences();
    else setKeyDifferences([]);
  }, [primaryDetails, secondaryDetails, determineKeyDifferences]);

  const fwBlock = (label, id, details) => (
    <div style={{ flex: 1, minWidth: 220, background: 'var(--tv-surface-2)', border: '0.5px solid var(--tv-border)', borderRadius: 8, padding: 14 }}>
      <div className="tv-dtac" style={{ margin: 0, color: 'var(--tv-defence)' }}>{label}</div>
      <div className="tv-did" style={{ fontSize: 15 }}>{id || '—'}</div>
      <div className="tv-rn" style={{ marginTop: 6 }}>{details ? details.name : 'No equivalent control found'}</div>
      {details?.description && <p className="tv-desc" style={{ fontSize: 12.5, marginTop: 8 }}>{details.description}</p>}
    </div>
  );

  return (
    <div className="tv-detail">
      <div className="tv-dh">
        <ArrowLeftRight style={{ width: 18, height: 18, color: 'var(--tv-defence)' }} />
        <h3>Framework Control Comparison</h3>
        <button className="tv-close" onClick={onClose} aria-label="Close comparison" style={{ marginLeft: 'auto' }}>
          <X style={{ width: 18, height: 18 }} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
        {fwBlock(primaryFramework, primaryControl, primaryDetails)}
        {fwBlock(secondaryFramework, secondaryControl, secondaryDetails)}
      </div>

      {keyDifferences.length > 0 && (
        <div className="tv-sec">
          <div className="tv-st">Key differences</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--tv-text)', fontSize: 13 }}>
            {keyDifferences.map((d, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                <span style={{ color: 'var(--tv-connection)' }}>{d.aspect}:</span> {d.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FrameworkComparison;
